import React, { useRef, useState } from "react";
import { 
	RCCButton, 
	RCCGrid, 
	RCCTooltip, 
	RCCInput,
	RCCServicesApi,
	RCCHookToast
} from "react-common-components";
import { useForm } from "react-hook-form";
import { useTranslation } from "src/context/Translations/context";
import FiltersContainer from "src/components/FiltersContainer";
import PageTitle from "src/components/PageTitle";
import { IndicatorLevel, IRefIndicatorModal } from "src/components/ModalIndicator/interface";
import IndicatorModal from "src/components/ModalIndicator";
import { standardErrorMessages } from "src/utils/errors";
import LovSectors from "src/components/Lov/Sectors";
import LovClassifications from "src/components/Lov/Classifications";
import LovUsers from "src/components/Lov/Users";
import Select from "src/components/Select";
import { IIndicatorHierarchyItem, IIndicatorListFilters, IRefIndicatorGrouper } from "src/pages/Indicators/Hierarchy/interface";
import IndicatorGrouper from "src/pages/Indicators/Hierarchy/components/grouper";
import { useConfirmModal } from "src/context/ConfirmModal/context";
import TreeHierarchyMaps from "src/components/ModalTreeHierarchyMaps/TreeHierarchyMaps";
import { IRefHierarchyMaps } from "src/components/ModalTreeHierarchyMaps/TreeHierarchyMaps/interface";
import AppIcons from "src/components/AppIcons";
import { useAuth } from "src/context/Auth/context";
import { PermissionEnum } from "src/context/Auth/interface";
import CheckPermission from "src/components/CheckPermission";
import { MapsHierarchyTypes } from "../Maps/interface";

const MapHierarchy: React.FC = () => { 
	const { translate } = useTranslation();
	const {addToast} = RCCHookToast();
	const {confirm} = useConfirmModal();
	const refModal = useRef<IRefIndicatorModal>(null);
	const refStrategic = useRef<IRefIndicatorGrouper>(null);
	const refTatic = useRef<IRefIndicatorGrouper>(null);
	const refOperational = useRef<IRefIndicatorGrouper>(null);
	const refMapHierarchy = useRef<IRefHierarchyMaps>(null);
	
	const groupers = [
		{id:IndicatorLevel.STRATEGIC,ref:refStrategic},
		{id:IndicatorLevel.TATIC,ref:refTatic},
		{id:IndicatorLevel.OPERATIONAL,ref:refOperational}
	];

	const { checkIfHasSpecificPermission } = useAuth();
	const hasPermissionToCreateIndicator = checkIfHasSpecificPermission("indicators/panel", PermissionEnum.CREATE_UPDATE);

	// Filters
	const [showFilters, setShowFilters] = useState(false);
	const [filters, setFilters] = useState<IIndicatorListFilters>({filter:"",situation:"T"});
	const { 
		handleSubmit : handleSubmitFilters, 
		register : registerFilters,
		control:controlFilters, 
		reset: resetFilters, 
		watch: watchFilters 
	} = useForm();

	const areUsingFilter = ():boolean => !!filters.classification || !!filters.filter || !!filters.responsible || !!filters.sector || filters.situation !== "T";

	const submitFilters = (data: any) => {
		setFilters(() => {
			const newState = {} as IIndicatorListFilters;
			newState.filter = data.filter;
			newState.situation = data.situation.value;

			if(data.classification)
				newState.classification = typeof data.classification === "object" ? data.classification?.value : data.classification;
			if(data.sector)
				newState.sector = typeof data.sector === "object" ? data.sector?.value : data.sector;
			if(data.responsible)
				newState.responsible = typeof data.responsible === "object" ? data.responsible?.value : data.responsible;

			return newState;
		});
	};

	const {   
		register,
		unregister
	} = useForm();

	const insertOnTree = async (row?:any) => {
		let selectedList = [] as Array<IIndicatorHierarchyItem>;

		const insertRequest = async(list:Array<IIndicatorHierarchyItem>) => {
			list.map((x) => {
				const y = x;
				y.iqe001Ordem = 0;
				return y;
			});

			const dados = {
				mea003Id: row.node.id,
				indicators: list
			};

			try {
				const dataController = await RCCServicesApi.postRequest({
					method: "Objectives/InsertBondIndicators",
					body: dados
				});
				return dataController.data as Array<IIndicatorHierarchyItem>;
	
			} catch (error) {
				standardErrorMessages(error, translate("Erro ao inserir itens na hierarquia"), addToast);
				return [];
			}
		};
		
		const taticList = refTatic.current?.getSelectedIndicators()||[];
		const operationalList = refOperational.current?.getSelectedIndicators()||[];
		const strategicList = refStrategic.current?.getSelectedIndicators()||[];
		
		selectedList = [...taticList, ...operationalList, ...strategicList];

		if(selectedList.length <= 0){
			addToast({
				color: "yellow",
				description: translate("Selecione ao menos um indicador"),
				title: translate("Aviso")
			});
		}

		// valida se já esta na lista
		const node = refMapHierarchy.current?.getNodePath(row.path);
		const listIndicatorsId = node?.children.map((x: any) => x.id2);
	
		const newList = selectedList.map((x) => {
			if(!listIndicatorsId?.includes(x.iqc001Id)){
				return x;
			}
			return null;
		}).filter((y) => y != null);

		const newSelectList = newList as Array<IIndicatorHierarchyItem>;

		if(newSelectList.length > 0){
			await insertRequest(newSelectList);
			refMapHierarchy.current?.addItem(row);
		}		

		// limpa os selecionados
		const cleanFunctionTatic = refTatic.current?.cleanSelectedIndicators;
		const cleanFunctionOperational = refOperational.current?.cleanSelectedIndicators;
		const cleanFunctionStrategic = refStrategic.current?.cleanSelectedIndicators;


		if(cleanFunctionTatic)
			cleanFunctionTatic(false);	

		if(cleanFunctionOperational)
			cleanFunctionOperational(false);	

		if(cleanFunctionStrategic)
			cleanFunctionStrategic(false);
	};

	const renderGroupers = () => 
		<>
			{groupers.map((grouper) => 
				<IndicatorGrouper 
					key={`${grouper.id}-grouper-indicators`}
					filters={filters}
					level={grouper.id}
					disabled={false}
					insertStrategicLevel={false}
					requestMethod="Indicators/ListIndicatorsHierarchy"
					filterHierarchy={false}
					ref={grouper.ref}
				/>
			)}
		</>
	;

	const removeItem = async(row: any) => {
		confirm(
			translate("Exclusão de Item da Hierarquia"), 
			`${translate("Deseja realmente excluir")}: ${row.node.name}?`,
			async () => {
				await RCCServicesApi.deleteRequest({
					method: "Objectives/DeleteBondIndicators",
					urlParameters:{
						mea003Id: row.parentNode.id,
						iqc001Id: row.node.id2
					}
				});

				refMapHierarchy.current?.removeItem(row.path);

				addToast({
					color: "green",
					description: translate("Item da hierarquia excluído com sucesso"),
					title: translate("Sucesso")
				});
			},
			(err) => {
				standardErrorMessages(err, translate("Erro ao excluir item da hierarquia"), addToast);
			},
			true
		);
	};

	const forceGrouperRequery = (newIndicator :any) => {
		const grouper = groupers.find((x) => x.id === newIndicator.indicator.iqc001Nivel);
		if(grouper)
			grouper.ref.current?.forceRequery();
	};

	const buttonMapHierarchy = (row :any) => 
		[<>
			{row.node.level === MapsHierarchyTypes.OBJECTIVES &&
				<CheckPermission minimumPermissionRequired={PermissionEnum.CREATE_UPDATE}>
					<RCCTooltip
						text={translate("Vincular indicadores")}
						color="green"
						position="leftEnd"
					>
						<RCCButton
							icon={{name: "arrowBack"}}
							color="green"
							layout="isRounded"
							size="sm"
							onClick={() => insertOnTree(row)}
						/>
					</RCCTooltip>
				</CheckPermission>
			}
			{
				row.node.level === MapsHierarchyTypes.INDICATORS &&
				<CheckPermission minimumPermissionRequired={PermissionEnum.CREATE_UPDATE_DELETE}>
					<RCCTooltip
						text={translate("Excluir Vínculo")}
						color="red"
						position="leftEnd"
					>
						<RCCButton
							icon={{name: "trash"}}
							color="red"
							layout="isRounded"
							size="sm"
							onClick={() => {removeItem(row);}}
						/>
					</RCCTooltip>
				</CheckPermission>          
			}				
		</>]
	;

	return (
		<RCCGrid container item
			spacing={5}
			direction="row-reverse"
		>
			<RCCGrid container item
				direction="column"
				sm={12}
				md={6}
			>
				<PageTitle
					title={translate("Indicadores")}
					icon={() => <AppIcons name="indicators" />}
				>
					<RCCTooltip
						text={translate("Filtros")}
						color={showFilters || areUsingFilter() ? "blue" : "grey"}
					>
						<RCCButton
							icon={{name: "filter", color: "#FFF", size: 16}}
							color={showFilters || areUsingFilter() ? "blue" : "grey"}
							layout="isRounded"
							size="md"
							onClick={() => setShowFilters((prevState) => !prevState)}
						/>
					</RCCTooltip>
					{hasPermissionToCreateIndicator && (
						<RCCTooltip
							text={translate("Novo indicador")}
							color="purple"
						>
							<RCCButton
								icon={{name: "plus", color: "#FFF", size: 18}}
								color="purple"
								layout="isRounded"
								onClick={() => { refModal?.current?.actionButtonAdd((newIndicator) => forceGrouperRequery(newIndicator));}}
								size="md"
							/>
						</RCCTooltip>
					)}
				</PageTitle>
				<FiltersContainer
					onSubmitFilters={handleSubmitFilters(submitFilters)}
					resetFilters={() => resetFilters({ 
						filter: "",
						sector:{value:"",label:""},
						responsible:{value:"",label:""},
						classification:{value:"",label:""},
						situation:{value: "T", label: translate("Todos")}
					})}
					showFilters={showFilters}
				>	
					<RCCGrid container item>
						<RCCGrid container item
							direction="row"
							spacing={1}
						>
							<RCCGrid container item
								xs={12}
								sm={4}
							>
								<LovSectors
									control={controlFilters}
									name="sector"
									label={translate("Setor")}
									isClearable
								/> 
							</RCCGrid>
							<RCCGrid container item
								xs={12}
								sm={4}
							>
								<LovClassifications
									control={controlFilters}
									name="classification"
									label={translate("Classificação")}
									isClearable
								/> 
							</RCCGrid>
							<RCCGrid
								container 
								item
								xs={12}
								sm={4}
							>
								<LovUsers
									control={controlFilters}
									name="responsible"
									label={translate("Responsável")}
									isClearable
								/>
							</RCCGrid>
							<RCCGrid container item
								xs={12}
								sm={4}
							>
								<Select 
									control={controlFilters}
									name="situation" 
									options={[
										{value: "A", label: translate("Ativo")}, 
										{value: "I", label: translate("Inativo")},
										{value: "T", label: translate("Todos")}
									]} 
									label={translate("Situação")}
									defaultValue={{value: "T", label: translate("Todos")}}
									submit="value and label"
								/>
							</RCCGrid>
							<RCCGrid
								container 
								item
								xs={12}
								sm={8}
							>
								<RCCInput
									name="filter" 
									label={translate("Pesquisar")}
									register={registerFilters}
									defaultValue={watchFilters("filter")}
								/>
							</RCCGrid>
						</RCCGrid>
					</RCCGrid>
				</FiltersContainer>
				<RCCGrid container
					styleInline="margin-top:0.5rem;">
					{renderGroupers()}
				</RCCGrid>
			</RCCGrid>
			<RCCGrid container item
				direction="column"
				sm={12}
				md={6}
			>
				<PageTitle
					title={translate("Hierarquia")}
					icon={() => <AppIcons name="strategicPlanning" />}
				/>
				<TreeHierarchyMaps
					ref={refMapHierarchy}
					register={register}
					unregister={unregister}
					checkLevelHierarchy = {[]}
					lastLevelHierarchy={MapsHierarchyTypes.INDICATORS}
					onCallbackButtonRow={buttonMapHierarchy}
				/>				
			</RCCGrid>
			<IndicatorModal ref={refModal} /> 
		</RCCGrid>
	);
};

export default MapHierarchy;
